import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowLeft, Shield, Eye, Database, Lock, Mail, Globe, Smartphone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_0c49a145-2431-460e-8df4-1bea13020ea4/artifacts/j4k88xee_file_00000000c9b871f8a2879288a4f57131.png";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  const sections = [
    {
      icon: Database,
      title: 'Information We Collect',
      content: [
        'Account information (email, username, profile picture)',
        'Content you create (posts, comments, messages, media)',
        'Usage data (how you interact with the app)',
        'Device information (device type, operating system)',
        'Location data (only if you enable location features)'
      ]
    },
    {
      icon: Eye,
      title: 'How We Use Your Information',
      content: [
        'To provide and improve our services',
        'To personalize your experience',
        'To communicate with you about updates and features',
        'To ensure safety and security of the platform',
        'To process payments for creator monetization'
      ]
    },
    {
      icon: Shield,
      title: 'How We Protect Your Data',
      content: [
        'Encryption of data in transit and at rest',
        'Secure authentication with JWT tokens',
        'Regular security audits and updates',
        'Access controls to limit data exposure',
        'Secure payment processing through PayPal'
      ]
    },
    {
      icon: Globe,
      title: 'Information Sharing',
      content: [
        'We do not sell your personal information',
        'Public posts are visible to all users',
        'Private messages are only visible to participants',
        'We may share data with law enforcement if required by law',
        'Third-party services (PayPal) have their own privacy policies'
      ]
    },
    {
      icon: Lock,
      title: 'Your Rights & Choices',
      content: [
        'Access and download your data',
        'Delete your account and associated data',
        'Control who can see your content (public/private)',
        'Opt out of promotional communications',
        'Block and report other users'
      ]
    },
    {
      icon: Smartphone,
      title: 'Mobile App Permissions',
      content: [
        'Camera: For taking photos and videos',
        'Microphone: For voice messages and video calls',
        'Storage: For saving and uploading media',
        'Notifications: For alerts about messages and activity',
        'All permissions are optional and can be revoked'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-xl font-bold">Privacy Policy</h1>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Logo and Intro */}
        <div className="text-center mb-12">
          <img src={LOGO_URL} alt="FityH" className="w-20 h-20 mx-auto rounded-2xl mb-4" />
          <h2 className="text-3xl font-bold mb-2">FityH Privacy Policy</h2>
          <p className="text-muted-foreground">Last updated: February 2026</p>
        </div>

        {/* Introduction */}
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-6 mb-8">
          <p className="text-lg leading-relaxed">
            At FityH, we are committed to protecting your privacy. This Privacy Policy explains how we collect, 
            use, and safeguard your information when you use our social platform. By using FityH, you agree to 
            the terms outlined in this policy.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, index) => (
            <div key={index} className="bg-card rounded-2xl border border-border/50 overflow-hidden">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                    <section.icon className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold">{section.title}</h3>
                </div>
                <ul className="space-y-3">
                  {section.content.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0" />
                      <span className="text-muted-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>

        {/* Children's Privacy */}
        <div className="mt-8 bg-yellow-500/10 border border-yellow-500/30 rounded-2xl p-6">
          <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Shield className="w-6 h-6 text-yellow-500" />
            Children's Privacy
          </h3>
          <p className="text-muted-foreground">
            FityH is not intended for children under 13 years of age. We do not knowingly collect personal 
            information from children under 13. If you are a parent or guardian and believe your child has 
            provided us with personal information, please contact us immediately.
          </p>
        </div>

        {/* Data Retention */}
        <div className="mt-8 bg-card rounded-2xl border border-border/50 p-6">
          <h3 className="text-xl font-bold mb-3">Data Retention</h3>
          <p className="text-muted-foreground mb-4">
            We retain your information for as long as your account is active or as needed to provide you services. 
            You can request deletion of your account and data at any time through the app settings or by contacting us.
          </p>
          <ul className="space-y-2 text-muted-foreground">
            <li>• Active accounts: Data retained while account is active</li>
            <li>• Deleted accounts: Data removed within 30 days</li>
            <li>• Backup data: Removed within 90 days</li>
            <li>• Legal obligations: Some data may be retained as required by law</li>
          </ul>
        </div>

        {/* Changes to Policy */}
        <div className="mt-8 bg-card rounded-2xl border border-border/50 p-6">
          <h3 className="text-xl font-bold mb-3">Changes to This Policy</h3>
          <p className="text-muted-foreground">
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
            the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review 
            this Privacy Policy periodically for any changes.
          </p>
        </div>

        {/* Contact */}
        <div className="mt-8 bg-primary/5 border border-primary/20 rounded-2xl p-6">
          <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Mail className="w-6 h-6 text-primary" />
            Contact Us
          </h3>
          <p className="text-muted-foreground mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2">
            <p className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-primary" />
              <a href="mailto:fityhapp@gmail.com" className="text-primary hover:underline">
                fityhapp@gmail.com
              </a>
            </p>
            <p className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              <a href="https://fityh.com" className="text-primary hover:underline">
                https://fityh.com
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-muted-foreground">
          <p>© 2026 FityH. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
}
